import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import MatterService from '@frontend/AppSettings/services/MatterService';
import OrganizationService from '@frontend/AppSettings/services/OrganizationService';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import toastService from '@frontend/AppSettings/services/ToastService';
import CreateStakeholderModal from './Components/CreateStakeholderModal/CreateStakeholderModal';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';
import CreateButton from '@frontend/Components/CreateButton/CreateButton';
import PageHeader from '@frontend/Components/Titles/PageHeader/PageHeader';
import OrbitFiltersSection from './Components/OrbitFiltersSection';
import OrbitStakeholdersTable from './Components/OrbitStakeholdersTable';
import type { Stakeholder, SourceTabType, TabType, OrbitFilterOptions } from './Components/types';
import './SEPTOrbitPage.css';

/**
 * SEPTOrbitPage Component
 *
 * Main page for managing individuals and organizations (stakeholders).
 * Data and state live here; UI is split into OrbitPageHeader, OrbitFiltersSection, OrbitStakeholdersTable.
 */
const SEPTOrbitPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSourceTab = (searchParams.get('tab') as SourceTabType) || 'all';
  const activeTypeTab = (searchParams.get('type') as TabType) || 'individuals';
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stakeholderToDelete, setStakeholderToDelete] = useState<Stakeholder | null>(null);
  const [filteredStakeholders, setFilteredStakeholders] = useState<Stakeholder[]>([]);
  const [assignedLeadFilter, setAssignedLeadFilter] = useState('all');
  const [organizationFilter, setOrganizationFilter] = useState('all');
  const [roleTitleFilter, setRoleTitleFilter] = useState('all');
  const [mattersStatusFilter, setMattersStatusFilter] = useState('all');
  const [primaryContactFilter, setPrimaryContactFilter] = useState('all');

  const { data: clients } = useQuery({
    queryKey: ['Clients', 'orbit'],
    queryFn: () => ClientService.getClients(),
  });

  const { data: agents } = useQuery({
    queryKey: ['Agents', 'orbit'],
    queryFn: () => AgentsService.getAgents(),
  });

  useQuery({
    queryKey: ['Employees', 'orbit'],
    queryFn: () => EmployeeService.getEmployees(),
  });

  const { data: matters } = useQuery({
    queryKey: ['Matters', 'orbit'],
    queryFn: () => MatterService.getMatters(),
  });

  // P2 Build J: REAL organizations (with member ids in one aggregated read) replace the
  // client-side company-string grouping. On any failure the page falls back to the
  // legacy string-groups below — prefer-new, fall-back-to-old.
  const { data: organizations } = useQuery({
    queryKey: ['Organizations', 'orbit'],
    queryFn: () => OrganizationService.getOrganizations(true),
  });

  const allStakeholdersData = useMemo(() => {
    const all: Stakeholder[] = [];

    // The /clients endpoint may return either the legacy snake_case
    // contact_created_date or the PascalCase ContactCreatedDate (after the
    // list_clients response was extended). Accept either so the table
    // shows up-to-date timestamps regardless of which is present.
    const parseClientLastActive = (client: any): Date | undefined => {
      const raw = client?.ContactCreatedDate ?? client?.contact_created_date;
      if (!raw) return undefined;
      if (typeof raw === 'string') {
        const parsed = new Date(raw);
        return Number.isNaN(parsed.getTime()) ? undefined : parsed;
      }
      return raw instanceof Date ? raw : undefined;
    };

    const individualClients = clients || [];
    individualClients.forEach((client: any) => {
      const clientId = String(client.ClientID ?? '');
      const clientMatters = (matters || []).filter(
        (m: any) => m.clientid === client.ClientID
      );
      const assignedLead =
        clientMatters.length > 0 && clientMatters[0].employeename
          ? clientMatters[0].employeename
          : '';

      all.push({
        id: clientId,
        name: `${client.FirstName || ''} ${client.LastName || ''}`.trim() || client.Company || 'Unknown',
        roleTitle: client.Title || client.Type || client.type || '',
        jurisdiction: client.Jurisdiction || '',
        primaryContact: {
          name: `${client.FirstName || ''} ${client.LastName || ''}`.trim() || 'N/A',
        },
        organization: client.Company || '',
        assignedLead,
        lastActive: parseClientLastActive(client),
        mattersCount: clientMatters.length,
        email: client.Email,
        phone: client.Phone,
        type: 'individual',
        source: 'client',
      });
    });

    (agents || []).forEach((agent: any) => {
      const agentId = String(agent.AgentID ?? '');

      all.push({
        id: agentId,
        name: `${agent.FirstName || ''} ${agent.LastName || ''}`.trim() || agent.Company || 'Unknown',
        roleTitle: agent.Title || '',
        jurisdiction: agent.Jurisdiction || '',
        primaryContact: {
          name: `${agent.FirstName || ''} ${agent.LastName || ''}`.trim() || 'N/A',
        },
        organization: agent.Company || '',
        assignedLead: '',
        lastActive: agent.ContactCreatedAt
          ? typeof agent.ContactCreatedAt === 'string'
            ? new Date(agent.ContactCreatedAt)
            : agent.ContactCreatedAt
          : undefined,
        mattersCount: 0,
        email: agent.Email,
        phone: agent.Phone,
        type: 'individual',
        source: 'agent',
      });
    });

    // ── P2 Build J: real organizations first ─────────────────────────────────────
    // One row per organizations record (id = the REAL org uuid); contact/matter display
    // data derives from the client/matter rows this page already loads, keyed by the
    // membership ids the org list ships (the N+1 killer). Clients whose company string
    // never got a join row render below as the DISCLOSED legacy group — never dropped.
    const clientById = new Map<string, any>(
      (clients || []).map((client: any) => [String(client.ClientID ?? ''), client])
    );
    const linkedClientIds = new Set<string>();
    (organizations || []).forEach((org) => {
      (org.member_clientids || []).forEach((cid) => linkedClientIds.add(cid));
    });

    (organizations || []).forEach((org) => {
      const memberIds = org.member_clientids || [];
      // member ids arrive primary-first from the API
      const anchor = memberIds.map((cid) => clientById.get(cid)).find(Boolean);
      const orgMatters = (matters || []).filter((m: any) => memberIds.includes(m.clientid));
      all.push({
        id: org.id,
        name: org.name,
        roleTitle: '',
        jurisdiction: '',
        primaryContact: {
          name: anchor
            ? `${anchor.FirstName || ''} ${anchor.LastName || ''}`.trim() || 'N/A'
            : 'N/A',
        },
        organization: org.name,
        assignedLead:
          orgMatters.length > 0 && orgMatters[0].employeename ? orgMatters[0].employeename : '',
        lastActive: anchor ? parseClientLastActive(anchor) : undefined,
        mattersCount: orgMatters.length,
        email: anchor?.Email,
        phone: anchor?.Phone,
        type: 'organization',
        source: 'client',
      });
    });

    // ── Disclosed legacy groups: clients with a company string but no join row ──
    const orgClients = (clients || []).filter(
      (client: any) =>
        client.Company &&
        client.Company !== '' &&
        !linkedClientIds.has(String(client.ClientID ?? ''))
    );
    const orgAgents = (agents || []).filter(
      (agent: any) => agent.Company && agent.Company !== ''
    );
    const orgMap = new Map<string, Stakeholder>();

    orgClients.forEach((client: any) => {
      const companyName = client.Company;
      if (!orgMap.has(companyName)) {
        const companyMatters = (matters || []).filter(
          (m: any) => m.clientid === client.ClientID
        );
        orgMap.set(companyName, {
          id: `org-${String(client.ClientID ?? '')}`,
          name: companyName,
          roleTitle: '',
          jurisdiction: '',
          primaryContact: {
            name: `${client.FirstName || ''} ${client.LastName || ''}`.trim() || 'N/A',
          },
          organization: companyName,
          assignedLead:
            companyMatters.length > 0 && companyMatters[0].employeename
              ? companyMatters[0].employeename
              : '',
          lastActive: parseClientLastActive(client),
          mattersCount: companyMatters.length,
          email: client.Email,
          phone: client.Phone,
          type: 'organization',
          source: 'client',
          legacy: true,
        });
      } else {
        const existing = orgMap.get(companyName)!;
        const companyMatters = (matters || []).filter(
          (m: any) => m.clientid === client.ClientID
        );
        existing.mattersCount = (existing.mattersCount || 0) + companyMatters.length;
      }
    });

    orgAgents.forEach((agent: any) => {
      const companyName = agent.Company;
      if (!orgMap.has(companyName)) {
        orgMap.set(companyName, {
          id: `org-${String(agent.AgentID ?? '')}`,
          name: companyName,
          roleTitle: '',
          jurisdiction: '',
          primaryContact: {
            name: `${agent.FirstName || ''} ${agent.LastName || ''}`.trim() || 'N/A',
          },
          organization: companyName,
          assignedLead: '',
          lastActive: agent.ContactCreatedAt
            ? typeof agent.ContactCreatedAt === 'string'
              ? new Date(agent.ContactCreatedAt)
              : agent.ContactCreatedAt
            : undefined,
          mattersCount: 0,
          email: agent.Email,
          phone: agent.Phone,
          type: 'organization',
          source: 'agent',
          // decision 7: agents stay string-grouped in v1, disclosed as such
          legacy: true,
        });
      }
    });

    all.push(...Array.from(orgMap.values()));
    return all;
  }, [clients, agents, matters, organizations]);

  const stakeholders = useMemo(() => {
    let list = allStakeholdersData;
    // Filter by source: All (both), Clients only, Other (agents) only
    if (activeSourceTab === 'clients') {
      list = list.filter((s) => s.source === 'client');
    } else if (activeSourceTab === 'other') {
      list = list.filter((s) => s.source === 'agent');
    }
    // Filter by type: individuals or organizations
    if (activeTypeTab === 'individuals') {
      return list.filter((s) => s.type === 'individual');
    }
    return list.filter((s) => s.type === 'organization');
  }, [allStakeholdersData, activeSourceTab, activeTypeTab]);

  useEffect(() => {
    if (stakeholders && stakeholders.length > 0) {
      let filtered = [...stakeholders];

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (s) =>
            s.name.toLowerCase().includes(searchLower) ||
            s.roleTitle?.toLowerCase().includes(searchLower) ||
            s.jurisdiction?.toLowerCase().includes(searchLower) ||
            s.email?.toLowerCase().includes(searchLower)
        );
      }

      if (assignedLeadFilter !== 'all') {
        filtered = filtered.filter((s) => s.assignedLead === assignedLeadFilter);
      }
      if (organizationFilter !== 'all') {
        filtered = filtered.filter((s) => s.organization === organizationFilter);
      }
      if (roleTitleFilter !== 'all') {
        filtered = filtered.filter((s) => s.roleTitle === roleTitleFilter);
      }
      if (primaryContactFilter !== 'all') {
        filtered = filtered.filter(
          (s) => s.primaryContact?.name === primaryContactFilter
        );
      }

      if (mattersStatusFilter !== 'all' && matters) {
        filtered = filtered.filter((s) => {
          let stakeholderMatters: any[] = [];
          if (activeTypeTab === 'individuals') {
            stakeholderMatters = matters.filter(
              (m: any) => m.clientid === s.id || m['Agent Reference'] === s.id
            );
          } else {
            const orgClients = clients?.filter((c: any) => c.Company === s.name) || [];
            const orgAgents = agents?.filter((a: any) => a.Company === s.name) || [];
            const orgClientIds = orgClients.map((c: any) => c.ClientID);
            const orgAgentIds = orgAgents.map((a: any) => a.AgentID);
            stakeholderMatters = matters.filter(
              (m: any) =>
                orgClientIds.includes(m.clientid) ||
                orgAgentIds.includes(m['Agent Reference'])
            );
          }
          return stakeholderMatters.some((m: any) => m.status === mattersStatusFilter);
        });
      }

      setFilteredStakeholders(filtered);
    } else {
      setFilteredStakeholders([]);
    }
  }, [
    stakeholders,
    searchTerm,
    assignedLeadFilter,
    organizationFilter,
    roleTitleFilter,
    primaryContactFilter,
    mattersStatusFilter,
    matters,
    activeTypeTab,
    clients,
    agents,
  ]);

  const paginatedStakeholders = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return filteredStakeholders.slice(startIndex, startIndex + pageSize);
  }, [filteredStakeholders, page, pageSize]);

  const pageLimit = useMemo(() => {
    return Math.max(1, Math.ceil(filteredStakeholders.length / pageSize));
  }, [filteredStakeholders.length, pageSize]);

  const filterOptions = useMemo((): OrbitFilterOptions => {
    if (
      !stakeholders ||
      !Array.isArray(stakeholders) ||
      stakeholders.length === 0
    ) {
      return {
        assignedLeads: [],
        organizations: [],
        roleTitles: [],
        matterStatuses: [],
        primaryContacts: [],
      };
    }

    const assignedLeads = new Set<string>();
    const organizations = new Set<string>();
    const roleTitles = new Set<string>();
    const matterStatuses = new Set<string>();
    const primaryContacts = new Set<string>();

    allStakeholdersData.forEach((s: Stakeholder) => {
      if (s.assignedLead && s.assignedLead.trim() !== '')
        assignedLeads.add(s.assignedLead);
      if (s.organization && s.organization.trim() !== '')
        organizations.add(s.organization);
      if (s.roleTitle && s.roleTitle.trim() !== '') roleTitles.add(s.roleTitle);
      if (
        s.primaryContact?.name &&
        s.primaryContact.name.trim() !== ''
      ) {
        primaryContacts.add(s.primaryContact.name);
      }
    });

    if (matters && Array.isArray(matters)) {
      matters.forEach((m: any) => {
        if (m.status && m.status.trim() !== '') matterStatuses.add(m.status);
      });
    }

    return {
      assignedLeads: Array.from(assignedLeads)
        .sort()
        .map((l) => ({ value: l, label: l })),
      organizations: Array.from(organizations)
        .sort()
        .map((o) => ({ value: o, label: o })),
      roleTitles: Array.from(roleTitles)
        .sort()
        .map((r) => ({ value: r, label: r })),
      matterStatuses: Array.from(matterStatuses)
        .sort()
        .map((s) => ({ value: s, label: s })),
      primaryContacts: Array.from(primaryContacts)
        .sort()
        .map((c) => ({ value: c, label: c })),
    };
  }, [stakeholders, matters, allStakeholdersData]);

  const handleStakeholderClick = useCallback(
    (stakeholder: Stakeholder) => {
      const type =
        stakeholder.type === 'individual' ? 'individual' : 'organization';
      const id = String(stakeholder.id ?? '');
      navigate(`/stakeholder/${type}/${id}`);
    },
    [navigate]
  );

  const handleOrganizationClick = useCallback(
    (organizationName: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const organization = allStakeholdersData.find(
        (s: Stakeholder) =>
          s.type === 'organization' && s.name === organizationName
      );
      if (organization) {
        const id = String(organization.id ?? '');
        navigate(`/stakeholder/organization/${id}`);
      }
    },
    [allStakeholdersData, navigate]
  );

  const handleDeleteStakeholder = useCallback(
    (stakeholder: Stakeholder) => {
      setStakeholderToDelete(stakeholder);
      setDeleteModalOpen(true);
    },
    []
  );

  const handleDeleteConfirm = async () => {
    if (!stakeholderToDelete) return;

    try {
      if (stakeholderToDelete.source === 'agent') {
        await AgentsService.deleteAgent(stakeholderToDelete.id);
      } else {
        await ClientService.deleteClient(stakeholderToDelete.id);
      }

      setDeleteModalOpen(false);
      setStakeholderToDelete(null);

      toastService.success('Client deleted successfully.');

      await queryClient.invalidateQueries({ queryKey: ['Clients', 'orbit'] });
      await queryClient.invalidateQueries({ queryKey: ['Agents', 'orbit'] });
    } catch (error) {
      console.error('Error deleting client:', error);
      toastService.error('Failed to delete client. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setStakeholderToDelete(null);
  };

  const handleSourceTabChange = (tabId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tabId);
      return next;
    });
    setPage(1);
    setSearchTerm('');
    setAssignedLeadFilter('all');
    setOrganizationFilter('all');
    setRoleTitleFilter('all');
    setMattersStatusFilter('all');
    setPrimaryContactFilter('all');
  };

  const handleTypeTabChange = (tabId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('type', tabId);
      return next;
    });
    setPage(1);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const clientsCount = useMemo(
    () => allStakeholdersData.filter((s) => s.source === 'client').length,
    [allStakeholdersData]
  );
  const otherCount = useMemo(
    () => allStakeholdersData.filter((s) => s.source === 'agent').length,
    [allStakeholdersData]
  );

  // Stakeholders for the currently selected source (All / Clients / Other) only
  const stakeholdersBySource = useMemo(() => {
    if (activeSourceTab === 'clients') {
      return allStakeholdersData.filter((s) => s.source === 'client');
    }
    if (activeSourceTab === 'other') {
      return allStakeholdersData.filter((s) => s.source === 'agent');
    }
    return allStakeholdersData;
  }, [allStakeholdersData, activeSourceTab]);

  // Individuals/Organizations counts scoped to the chosen source toggle
  const individualsCount = useMemo(
    () => stakeholdersBySource.filter((s) => s.type === 'individual').length,
    [stakeholdersBySource]
  );
  const organizationsCount = useMemo(
    () => stakeholdersBySource.filter((s) => s.type === 'organization').length,
    [stakeholdersBySource]
  );

  const sourceTabs = useMemo(
    () => [
      { id: 'all', label: 'All', count: clientsCount + otherCount },
      { id: 'clients', label: 'Clients', count: clientsCount },
      { id: 'other', label: 'Other', count: otherCount },
    ],
    [clientsCount, otherCount]
  );

  const typeTabs = useMemo(
    () => [
      { id: 'individuals', label: 'Individuals', count: individualsCount },
      { id: 'organizations', label: 'Organizations', count: organizationsCount },
    ],
    [individualsCount, organizationsCount]
  );

  const totalCount = stakeholders.length;
  const tabLabel = activeTypeTab === 'individuals' ? 'Individuals' : 'Organizations';

  return (
    <div className="sept-orbit-page">
      <PageHeader
        title="SEPT Orbit"
        subtitle="Your network of individuals and organizations"
      >
        <CreateButton
          onClick={() => setIsCreateModalOpen(true)}
          text={activeTypeTab === 'organizations' ? 'Create Organization' : 'Create Stakeholder'}
          showIcon
          aria-label={activeTypeTab === 'organizations' ? 'Create organization' : 'Create stakeholder'}
        />
      </PageHeader>

      <OrbitFiltersSection
        sourceTabs={sourceTabs}
        activeSourceTab={activeSourceTab}
        onSourceTabChange={handleSourceTabChange}
        typeTabs={typeTabs}
        activeTypeTab={activeTypeTab}
        onTypeTabChange={handleTypeTabChange}
        onSearch={(term) => {
          setSearchTerm(term);
          setPage(1);
        }}
        searchPlaceholder={`Search ${tabLabel}...`}
        filterOptions={filterOptions}
        assignedLeadFilter={assignedLeadFilter}
        onAssignedLeadFilterChange={(value) => {
          setAssignedLeadFilter(value);
          setPage(1);
        }}
        primaryContactFilter={primaryContactFilter}
        onPrimaryContactFilterChange={(value) => {
          setPrimaryContactFilter(value);
          setPage(1);
        }}
        organizationFilter={organizationFilter}
        onOrganizationFilterChange={(value) => {
          setOrganizationFilter(value);
          setPage(1);
        }}
        roleTitleFilter={roleTitleFilter}
        onRoleTitleFilterChange={(value) => {
          setRoleTitleFilter(value);
          setPage(1);
        }}
        mattersStatusFilter={mattersStatusFilter}
        onMattersStatusFilterChange={(value) => {
          setMattersStatusFilter(value);
          setPage(1);
        }}
      />

      <OrbitStakeholdersTable
        data={paginatedStakeholders}
        activeSourceTab={activeSourceTab}
        activeTypeTab={activeTypeTab}
        emptyMessage={`No ${tabLabel.toLowerCase()} found`}
        onStakeholderClick={handleStakeholderClick}
        onOrganizationClick={handleOrganizationClick}
        onDeleteStakeholder={handleDeleteStakeholder}
        pagination={{
          currPage: page,
          onPageChange: setPage,
          pageLimit,
          pageSize,
          onPageSizeChange: handlePageSizeChange,
          totalItems: filteredStakeholders.length,
          itemLabel: isEngineeringEnvironment ? 'projects' : 'matters',
        }}
      />

      <CreateStakeholderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        stakeholderType={activeTypeTab === 'individuals' ? 'individual' : 'organization'}
      />

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        message={
          stakeholderToDelete
            ? `Are you sure you want to delete client "${stakeholderToDelete.name}"? This will permanently delete all associated matters${
                stakeholderToDelete.mattersCount
                  ? ` (${stakeholderToDelete.mattersCount})`
                  : ''
              }, tasks, financial records, invoices, and other related data. This action cannot be undone.`
            : 'Are you sure you want to delete this record?'
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default SEPTOrbitPage;
